import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { SEOHead } from "@/components/SEOHead";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  image_url: string | null;
  published_at: string;
  created_at: string;
  author_name: string | null;
  tags: string[] | null;
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  // Extract unique years from posts
  const availableYears = useMemo(() => {
    const years = posts.map(post => new Date(post.published_at).getFullYear());
    const uniqueYears = [...new Set(years)].sort((a, b) => b - a); // Sort descending
    return uniqueYears;
  }, [posts]);

  // Filter posts by selected year
  const filteredPosts = useMemo(() => {
    if (selectedYear === "all") return posts;
    return posts.filter(post => 
      new Date(post.published_at).getFullYear().toString() === selectedYear
    );
  }, [posts, selectedYear]);

  const fetchPosts = async () => {
    const { data } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false });
    
    setPosts(data || []);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead slug="blog" />
      <div className="container max-w-7xl py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-3xl font-semibold text-foreground">
            Our Blog Posts
          </h1>
          {availableYears.length > 0 && (
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">
              {posts.length === 0 ? "No blog posts yet. Check back soon!" : "No blog posts found for this year."}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.id} 
                className="group cursor-pointer border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow bg-card"
                onClick={() => navigate(`/blog/${post.id}`)}
              >
                {/* Image */}
                <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                  {post.image_url ? (
                    <img 
                      src={post.image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      No image
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Author and Date */}
                  <div className="text-sm text-muted-foreground mb-2">
                    {post.author_name && (
                      <>
                        <span className="font-medium text-foreground">{post.author_name}</span>
                        <span className="mx-2">•</span>
                      </>
                    )}
                    <span>{formatDate(post.published_at)}</span>
                  </div>

                  {/* Title with Arrow */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h2 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">
                      {post.title}
                    </h2>
                    <ArrowUpRight className="h-5 w-5 flex-shrink-0 text-foreground mt-1 group-hover:text-primary transition-colors" />
                  </div>

                  {/* Excerpt */}
                  <p className="text-muted-foreground line-clamp-3 mb-4 leading-relaxed">
                    {post.excerpt}
                  </p>

                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, idx) => (
                        <Badge 
                          key={idx} 
                          variant="outline" 
                          className="rounded-full font-normal text-sm px-3 py-1"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
